/**
 * This file was adapted from confluence/next/packages/search-utils/src/excerpt.tsx
 */

import React, { type ReactElement } from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { type PersonCardProps } from '@atlassian/search-ui/card';

import { type ThirdPartyConfigsBootstrap } from '../constants/schemas/3p-config';
import { getIntegrationKey } from './get-integration-key';
import { getProductFromProviderId } from './get-product-from-provider-id';
import { replaceHtmlEntities } from './replace-html-entities';
import { secondsSinceDate } from './seconds-since-date';
import { SHAREPOINT_RESOURCE_TYPES } from './sharepoint-resource-types';
import { TEAMS_RESOURCE_TYPES } from './teams-resource-types';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { replaceHtmlEntities } from './replace-html-entities';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getWorkspaceNameFromProduct } from './get-workspace-name-from-product';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isNonPublic } from './is-non-public';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { truncateDescription } from './truncate-description';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isAWSThumbnailUrlExpired } from './is-aws-thumbnail-url-expired';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { secondsSinceDate } from './seconds-since-date';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getIntegrationKey } from './get-integration-key';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getUserData } from './get-user-data';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getProductFromProviderId } from './get-product-from-provider-id';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { SHAREPOINT_RESOURCE_TYPES } from './sharepoint-resource-types';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { TEAMS_RESOURCE_TYPES } from './teams-resource-types';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getProductDisplayNameFromProviderId } from './get-product-display-name-from-provider-id';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { filterSpacer } from './filter-spacer';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isProductImpersonated } from './is-product-impersonated';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getJiraSearchResultTypeMetadata } from './get-jira-search-result-type-metadata';

const SPECIAL_CHARS_REGEX = /^[!#$%^&*() ,.<>?\/\[\]_\-+=`~\|:;{}\\]+/;

const MAX_EXCERPT_LENGTH = 80;
/**
 * Make excerpt more readable by stripping leading non-alpha characters,
 * adding an ellipsis prefix and suffix, replacing line breaks with separators,
 * and bolding search terms.
 */

// TODO: deprecate this function in favor of enrichExcerpt
export const processExcerpt: (
	excerpt?: string | null,
	noLeadingEllipsis?: boolean,
	prefix?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	excerpt?: string | null,
	noLeadingEllipsis?: boolean,
	prefix?: string,
): React.JSX.Element | React.JSX.Element[] => {
	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	excerpt = replaceHtmlEntities(excerpt).trim();

	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	const beginsWithHighlight = excerpt.slice(0, 8) === '@@@hl@@@';
	const beginsWithUrl =
		excerpt.slice(0, 4) === 'http' || (beginsWithHighlight && excerpt.slice(8, 12) === 'http');

	// Strip leading special characters unless the excerpt begins with a highlight
	if (!beginsWithHighlight) {
		excerpt = excerpt.replace(SPECIAL_CHARS_REGEX, '');
	}

	// Add ellipsis prefix if beginning of excerpt isn't a URL and excerpt length is greater than 80
	if (!noLeadingEllipsis && !beginsWithUrl && excerpt.length > MAX_EXCERPT_LENGTH) {
		excerpt = '...'.concat(excerpt);
	}

	// Add ellipsis suffix if excerpt doesn't end with ending punctuation and if length is greater than 80
	if (excerpt.length > MAX_EXCERPT_LENGTH && !excerpt.slice(-1).match(/[.!?]/)) {
		excerpt = excerpt.concat('...');
	}

	// Replace line breaks with separators
	excerpt = excerpt.replace(/\n+/g, ' · ');
	return enrichExcerpt(excerpt, prefix);
};

// Utility function to insert zero-width space every N characters
const insertZeroWidthSpace = (str: string, interval: number) => {
	let result = '';
	for (let i = 0; i < str.length; i++) {
		result += str[i];
		if ((i + 1) % interval === 0) {
			result += '\u200B';
		}
	}
	return result;
};

// Adding zero-width spaces to prevent overflow on long words or sequence of characters
export const processSegment: (segment: string) => string = (segment: string): string => {
	return segment
		.split(' ')
		.map((word) => {
			if (word.length > 20) {
				return insertZeroWidthSpace(word, 5);
			}
			return word;
		})
		.join(' ');
};
/**
 * Replaces highlight markers with <strong> tags.
 * Typically used for highlighting search terms in excerpts.
 */
export const enrichExcerpt: (
	excerpt?: string | null,
	prefix?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	excerpt?: string | null,
	prefix?: string,
): React.JSX.Element | React.JSX.Element[] => {
	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	excerpt = replaceHtmlEntities(excerpt).trim();

	if (!excerpt) {
		return [<Text key="empty">{''}</Text>];
	}

	const res: ReactElement[] = [];
	let pre, textMatch;
	let i = 0;
	while (excerpt) {
		const match: RegExpMatchArray | null = excerpt.match(/(.*?)@@@hl@@@(.*?)@@@endhl@@@(.*)/s);
		if (!match) {
			res.push(
				<Box as="span" key={excerpt}>
					{processSegment(excerpt)}
				</Box>,
			);
			break;
		}
		[, pre, textMatch, excerpt] = match;
		if (pre) {
			res.push(
				<Box as="span" key={`pre-${i}`}>
					{processSegment(pre)}
				</Box>,
			);
		}
		if (textMatch) {
			res.push(
				<Box as="strong" key={`textMatch-${i}`}>
					{processSegment(textMatch)}
				</Box>,
			);
		}
		i++;
	}
	return (
		// NOTE: wrapping these in <div> previously broke focus rings
		// in search results
		<>
			{prefix}
			{res}
		</>
	);
};

type TimeSinceDate = {
	years: number;
	days: number;
	hours: number;
	minutes: number;
	months: number;
	seconds: number;
};

type RelativeTime = {
	unit: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
	value: number;
};

export const getRelativeTimeSince: (timestamp: Date | undefined) => RelativeTime = (
	timestamp: Date | undefined,
): RelativeTime => {
	return getTimeValuesWrapper(timeSinceDate(timestamp));
};

export const timeSinceDate: (timestamp: Date | undefined) => TimeSinceDate = (
	timestamp: Date | undefined,
): TimeSinceDate => {
	if (!timestamp) {
		timestamp = new Date();
	}
	const secondsPast = secondsSinceDate(timestamp);

	const timeUnits = {
		seconds: secondsPast,
		minutes: secondsPast / 60,
		hours: secondsPast / 3600,
		days: secondsPast / 86400,
		months: secondsPast / 2592000,
		years: secondsPast / 31536000,
	};

	return {
		seconds: Math.floor(timeUnits.seconds),
		minutes: Math.floor(timeUnits.minutes),
		hours: Math.floor(timeUnits.hours),
		days: Math.floor(timeUnits.days),
		months: Math.floor(timeUnits.months),
		years: Math.floor(timeUnits.years),
	};
};

export const getTimeValuesWrapper: ({
	seconds,
	minutes,
	hours,
	days,
	months,
	years,
}: {
	seconds: number;
	minutes: number;
	hours: number;
	days: number;
	months: number;
	years: number;
}) => RelativeTime = ({
	seconds,
	minutes,
	hours,
	days,
	months,
	years,
}: {
	seconds: number;
	minutes: number;
	hours: number;
	days: number;
	months: number;
	years: number;
}) => {
	return getTimeValues(seconds, minutes, hours, days, months, years);
};

export const getTimeValues: (
	seconds: number,
	minutes: number,
	hours: number,
	days: number,
	months: number,
	years: number,
) => RelativeTime = (
	seconds: number,
	minutes: number,
	hours: number,
	days: number,
	months: number,
	years: number,
): RelativeTime => {
	if (years > 0) {
		return { unit: 'year', value: years };
	} else if (months > 0) {
		return { unit: 'month', value: months };
	} else if (days > 0) {
		return { unit: 'day', value: days };
	} else if (hours > 0) {
		return { unit: 'hour', value: hours };
	} else if (minutes > 0) {
		return { unit: 'minute', value: minutes };
	} else {
		return { unit: 'second', value: seconds };
	}
};

type UserWithMaybeDmInfo = {
	email?: string | null;
	externalId?: string | null;
};
/**
 * @returns information for the feature to Direct Message the user
 */
export const getExternalMessageDmIntegrationInfo: (
	integrationId?: string | null,
	user?: UserWithMaybeDmInfo,
) => PersonCardProps['externalMessageIntegration'] | undefined = (
	integrationId?: string | null,
	user?: UserWithMaybeDmInfo,
): PersonCardProps['externalMessageIntegration'] | undefined => {
	if (!integrationId || !user) {
		return;
	}

	const isSlack = integrationId === 'ari:cloud:platform::integration/slack';
	const isMsTeams = integrationId === 'ari:cloud:platform::integration/microsoft';

	if (isSlack) {
		const openDmUrl = getOpenInSlackUrl(user);
		if (openDmUrl) {
			return { integration: 'slack', openDmUrl };
		}
	} else if (isMsTeams) {
		const openDmUrl = getOpenInTeamsUrl(user);
		if (openDmUrl) {
			return { integration: 'ms-teams', openDmUrl };
		}
	}
};

/**
 * @returns Open in Slack URL for the user
 */
export const getOpenInSlackUrl: (user?: UserWithMaybeDmInfo) => string | undefined = (
	user?: UserWithMaybeDmInfo,
) => {
	const externalId = user?.externalId;
	if (!externalId) {
		return;
	}
	return `https://slack.com/app_redirect?channel=${externalId}`;
};

/**
 * @returns Open in MS Teams URL for the user
 */
export const getOpenInTeamsUrl: (user?: UserWithMaybeDmInfo) => string | undefined = (
	user?: UserWithMaybeDmInfo,
) => {
	const email = user?.email;
	if (!email) {
		return;
	}
	return `https://teams.microsoft.com/l/chat/0/0?users=${email}`;
};

export const MICROSOFT_INTEGRATION_KEY = 'microsoft';
/**
 * Resolves a ProductKeys3P for Microsoft results that share the same integrationAri.
 * Extracts the same resourceType-based disambiguation logic used in IconFor3P.
 */
export const getProductFromMicrosoftResourceType: (
	integrationKey: string | undefined,
	resourceType: string | null | undefined,
) => ProductKeys3P | undefined = (
	integrationKey: string | undefined,
	resourceType: string | null | undefined,
): ProductKeys3P | undefined => {
	if (integrationKey !== MICROSOFT_INTEGRATION_KEY || !resourceType) {
		return undefined;
	}
	if (SHAREPOINT_RESOURCE_TYPES.includes(resourceType)) {
		return 'sharepoint' as ProductKeys3P;
	}
	if (TEAMS_RESOURCE_TYPES.includes(resourceType)) {
		return 'teams' as ProductKeys3P;
	}
	return undefined;
};

export const get3PProductFromProviderIdOrIntegrationId: ({
	thirdPartyConfigs,
	providerId,
	integrationId,
	resourceType,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	providerId: string | null | undefined;
	integrationId: string | null | undefined;
	resourceType?: string | null;
}) => ProductKeys3P | undefined = ({
	thirdPartyConfigs,
	providerId,
	integrationId,
	resourceType,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	providerId: string | null | undefined;
	integrationId: string | null | undefined;
	resourceType?: string | null;
}): ProductKeys3P | undefined => {
	const integrationKey = getIntegrationKey(integrationId);
	return (
		// Last-resort fallback: returns the raw integration key (e.g. 'slack', 'github').
		// For Microsoft products without a matching resourceType (e.g. OneDrive), this returns
		// 'microsoft' which has no displayName — intentional, as we can't disambiguate further.
		getProductFromProviderId(thirdPartyConfigs, providerId) ||
		getProductFromMicrosoftResourceType(integrationKey, resourceType) ||
		(integrationKey as ProductKeys3P | undefined)
	);
};
